import React, { useState, useEffect } from 'react';
import { formatDistance } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { db } from './firebase';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  updateDoc, 
  doc, 
  orderBy, 
  limit, 
  onSnapshot,
  Timestamp,
  writeBatch,
  increment,
  getDoc,
  setDoc
} from '@firebase/firestore';
import { generateAIMessage } from './openai';
import Background from './components/Background';

interface Participant {
  address: string;
  timestamp: Date;
  completedTasks: number[];
  tickets: number;
}

interface Task {
  id: number;
  title: string;
  completed: boolean;
}

interface ChatMessage {
  id?: string;
  content: string;
  timestamp: Date;
}

declare global {
  interface Window {
    phantom?: {
      solana?: {
        connect(): Promise<{ publicKey: { toString(): string } }>;
        disconnect(): Promise<void>;
        isConnected: boolean;
        on(event: string, callback: (args: any) => void): void;
      };
    };
  }
}

interface CustomAlertProps {
  message: string;
  onClose: () => void;
}

const CustomAlert: React.FC<CustomAlertProps> = ({ message, onClose }) => (
  <div className="fixed inset-0 flex items-center justify-center z-50">
    <div className="absolute inset-0 bg-retro-black/70 backdrop-blur-sm" onClick={onClose}/>
    <div className="relative bg-retro-black/95 border border-retro-white/30 rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
      <div className="flex flex-col items-center space-y-4">
        <p className="text-retro-white text-center">{message}</p>
        <button
          onClick={onClose}
          className="bg-retro-black/40 hover:bg-retro-black/60 text-retro-white font-medium py-2 px-8 rounded-lg transition-all duration-300 hover:scale-[1.02] border border-retro-white/30 hover:border-retro-white backdrop-blur-sm"
        >
          OK
        </button>
      </div>
    </div>
  </div>
);

function App() {
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [targetTimestamp, setTargetTimestamp] = useState<number>(1738017000); 
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState<string>('');
  const [showLotteryCard, setShowLotteryCard] = useState(false);
  const [showAnimation, setShowAnimation] = useState(true);
  const [tasks, setTasks] = useState<Task[]>([
    { id: 1, title: 'Follow us on Twitter', completed: false },
    { id: 2, title: 'RT our lottery announcement', completed: false },
    { id: 3, title: 'Post your lottery entry', completed: false }
  ]);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastMessageTimestamp, setLastMessageTimestamp] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<'participants' | 'messages'>('participants');
  const [globalStats, setGlobalStats] = useState({
    total_participants: 0,
    total_tickets: 0
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = Math.floor(Date.now() / 1000);
      const difference = targetTimestamp - now;
      return difference > 0 ? difference : 0;
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [targetTimestamp]);

  // Effet séparé pour les messages OpenAI
  useEffect(() => {
    const INTERVAL = 300000; // 5 minutes en millisecondes
    let lastMessageTime = Date.now();

    const generateMessage = async () => {
      const currentTime = Date.now();
      if (currentTime - lastMessageTime >= INTERVAL) {
        const lotteryInfo = {
          timeLeft: formatTime(timeLeft),
          participants: globalStats.total_participants,
          totalTickets: globalStats.total_tickets,
          prizePool: "5 SOL",
          topics: [
            "Welcome to Soul AI Lottery",
            "How our AI-powered lottery works",
            "Benefits of participating",
            "Current prize pool status",
            "Community engagement updates",
            "Tips for increasing winning chances"
          ]
        };

        try {
          const message = await generateAIMessage(lotteryInfo);
          await addChatMessage(message);
          lastMessageTime = currentTime;
        } catch (error) {
          console.error("Error generating AI message:", error);
        }
      }
    };

    const timer = setInterval(generateMessage, 30000);
    generateMessage(); // Generate first message immediately

    return () => clearInterval(timer);
  }, [timeLeft, globalStats]);

  const formatTime = (seconds: number): string => {
    if (seconds <= 0) return "0j 0h 0m 0s";
    
    const days = Math.floor(seconds / (24 * 60 * 60));
    const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((seconds % (60 * 60)) / 60);
    const remainingSeconds = seconds % 60;

    return `${days}d ${hours}h ${minutes}m ${remainingSeconds}s`;
  };

  useEffect(() => {
    // Arrêter l'animation après 2 secondes
    const timer = setTimeout(() => {
      setShowAnimation(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, [showLotteryCard]);

  useEffect(() => {
    if (walletConnected) {
      loadParticipantData();
    }
  }, [walletConnected]);

  // Ajout du chargement initial des participants
  useEffect(() => {
    loadAllParticipants();
  }, []);

  const loadAllParticipants = async () => {
    try {
      const participantsRef = collection(db, 'participants');
      const participantsSnapshot = await getDocs(participantsRef);
      
      const participantsList = participantsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          address: data.address,
          timestamp: data.timestamp.toDate(),
          completedTasks: data.completedTasks || [],
          tickets: data.tickets || 0
        } as Participant;
      });

      participantsList.sort((a, b) => b.tickets - a.tickets);
      
      setParticipants(participantsList);
    } catch (error) {
      // Silently handle error
    }
  };

  const loadParticipantData = async () => {
    if (!walletAddress) return;
    
    const participantsRef = collection(db, 'participants');
    const q = query(participantsRef, where('address', '==', walletAddress));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const participantData = querySnapshot.docs[0].data() as Participant;
      setTasks(tasks.map(task => ({
        ...task,
        completed: participantData.completedTasks.includes(task.id)
      })));
    }
  };

  const connectWallet = async () => {
    try {
      const provider = window.phantom?.solana;

      if (!provider) {
        window.open('https://phantom.app/', '_blank');
        return;
      }

      const response = await provider.connect();
      const address = response.publicKey.toString();
      setWalletAddress(address);
      setWalletConnected(true);

      const participantsRef = collection(db, 'participants');
      const q = query(participantsRef, where('address', '==', address));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const participantData = querySnapshot.docs[0].data() as Participant;
        setTasks(tasks.map(task => ({
          ...task,
          completed: participantData.completedTasks.includes(task.id)
        })));
      }

      await loadAllParticipants();

    } catch (error) {
      // Silently handle error
    }
  };

  const disconnectWallet = async () => {
    try {
      const provider = window.phantom?.solana;
      if (provider) {
        await provider.disconnect();
        setWalletAddress('');
        setWalletConnected(false);
        setTasks([
          { id: 1, title: 'Follow us on Twitter', completed: false },
          { id: 2, title: 'RT our lottery announcement', completed: false },
          { id: 3, title: 'Post your lottery entry', completed: false }
        ]);
        setShowLotteryCard(false);
      }
    } catch (error) {
      // Silently handle error
    }
  };

  const handleTwitterAction = async (taskId: number) => {
    if (!walletConnected) {
      setAlertMessage("Please connect your wallet first!");
      return;
    }

    let twitterUrl = '';
    switch (taskId) {
      case 1:
        twitterUrl = 'hhttps://x.com/SoulSeekAi';
        break;
      case 2:
        twitterUrl = 'https://twitter.com/intent/retweet?tweet_id=';
        break;
      case 3:
        const tweetText = encodeURIComponent("I'm participating in @SoulSeekAi Lottery #0001! 🎟️✨ #SoulAI #Lottery");
        twitterUrl = `https://twitter.com/intent/tweet?text=${tweetText}`;
        break;
      default:
        return;
    }

    try {
      // Ouvrir la fenêtre Twitter
      const twitterWindow = window.open(twitterUrl, '_blank');
      
      // Attendre que l'utilisateur confirme l'action
      setAlertMessage("Please confirm when you've completed the Twitter action");
      
      // Mettre à jour la tâche et les tickets
      await updateParticipantTasks(taskId);

      // Mettre à jour l'interface utilisateur
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId ? { ...task, completed: true } : task
        )
      );

      // Message de succès spécifique à la tâche
      const taskMessages = {
        1: '✨ Thanks for following us! +1 ticket added to your collection 🎟️',
        2: '✨ Thanks for the RT! +1 ticket added to your collection 🎟️',
        3: '✨ Thanks for spreading the word! +1 ticket added to your collection 🎟️'
      };

      setTimeout(() => {
        setAlertMessage(taskMessages[taskId as keyof typeof taskMessages]);
      }, 1500);

    } catch (error) {
      console.error("Error during Twitter action:", error);
      setAlertMessage("An error occurred. Please try again ✨");
    }
  };

  // Effet pour écouter les changements des participants en temps réel
  useEffect(() => {
    const participantsRef = collection(db, 'participants');
    const q = query(participantsRef, orderBy('timestamp', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const participantsList = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          address: data.address,
          timestamp: data.timestamp.toDate(),
          completedTasks: data.completedTasks || [],
          tickets: data.tickets || 0
        } as Participant;
      });

      participantsList.sort((a, b) => b.tickets - a.tickets);
      setParticipants(participantsList);
    });

    return () => unsubscribe();
  }, []);

  // Ajouter un nouveau message dans Firebase
  const addChatMessage = async (content: string) => {
    const messagesRef = collection(db, 'chat_messages');
    const timestamp = new Date();
    
    try {
      await addDoc(messagesRef, {
        content,
        timestamp
      });
    } catch (error) {
      console.error("Error adding message:", error);
    }
  };

  // Écouteur pour les stats globales
  useEffect(() => {
    const statsRef = doc(db, 'global_stats', 'lottery_stats');
    
    const unsubscribe = onSnapshot(statsRef, (doc) => {
      if (doc.exists()) {
        setGlobalStats({
          total_participants: doc.data().total_participants || 0,
          total_tickets: doc.data().total_tickets || 0
        });
      }
    });

    return () => unsubscribe();
  }, []);

  // Mettre à jour les statistiques globales
  const updateGlobalStats = async (isNewParticipant: boolean, ticketsAdded: number) => {
    try {
      const statsRef = doc(db, 'global_stats', 'lottery_stats');
      const statsDoc = await getDoc(statsRef);
      
      const updateData = {
        total_participants: increment(isNewParticipant ? 1 : 0),
        total_tickets: increment(ticketsAdded)
      };

      if (statsDoc.exists()) {
        await updateDoc(statsRef, updateData);
      } else {
        await setDoc(statsRef, {
          total_participants: isNewParticipant ? 1 : 0,
          total_tickets: ticketsAdded
        });
      }
    } catch (error) {
      console.error("Error updating global stats:", error);
    }
  };

  const updateParticipantTasks = async (taskId: number) => {
    if (!walletAddress) {
      setAlertMessage("Please connect your wallet first!");
      return;
    }

    try {
      const participantsRef = collection(db, 'participants');
      const q = query(participantsRef, where('address', '==', walletAddress));
      const querySnapshot = await getDocs(q);

      const timestamp = Timestamp.fromDate(new Date()); // Utiliser Timestamp de Firebase
      const batch = writeBatch(db);

      if (querySnapshot.empty) {
        // Nouveau participant
        const newParticipantRef = doc(participantsRef);
        const participantData = {
          address: walletAddress,
          completedTasks: [taskId], // Commencer avec la première tâche
          tickets: 1, // Commencer avec 1 ticket
          timestamp: timestamp
        };

        batch.set(newParticipantRef, participantData);

        // Mettre à jour les stats globales
        await updateGlobalStats(true, 1);
        await addChatMessage(`🎉 Welcome to Soul AI Lottery! ${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)} joined with $SOUL tokens!`);
      } else {
        // Participant existant
        const participantDoc = querySnapshot.docs[0];
        const currentData = participantDoc.data();
        const currentCompletedTasks = currentData.completedTasks || [];
        
        if (!currentCompletedTasks.includes(taskId)) {
          const newCompletedTasks = [...currentCompletedTasks, taskId].sort((a, b) => a - b); // Trier les tâches numériquement
          const newTickets = newCompletedTasks.length;

          const updateData = {
            completedTasks: newCompletedTasks,
            tickets: newTickets,
            timestamp: timestamp // Mettre à jour le timestamp
          };

          batch.update(participantDoc.ref, updateData);

          // Mettre à jour les stats globales
          await updateGlobalStats(false, 1);
          await addChatMessage(`🎟️ ${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)} earned a new ticket! Total: ${newTickets} tickets`);
        }
      }

      // Exécuter le batch
      await batch.commit();

      // Mettre à jour l'interface utilisateur localement
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId ? { ...task, completed: true } : task
        )
      );

    } catch (error) {
      console.error("Error updating participant:", error);
      throw error;
    }
  };

  const toggleTask = (taskId: number) => {
    setTasks(tasks.map(task => 
      task.id === taskId ? { ...task, completed: !task.completed } : task
    ));
  };

  const allTasksCompleted = tasks.every(task => task.completed);

  const LotteryCardModal = () => (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"/>

      {/* Conteneur principal avec image de fond */}
      <div className="relative w-full max-w-[900px] h-[300px] xs:h-[400px] sm:h-[500px] md:h-[600px] mx-auto">
        {/* Image de fond */}
        <div 
          className="absolute inset-0 bg-cover bg-center rounded-lg overflow-hidden shadow-2xl"
          style={{ backgroundImage: 'url(/ban.jpeg)' }}
        >
          {/* Close button */}
          <button 
            onClick={() => setShowLotteryCard(false)}
            className="absolute top-2 sm:top-4 right-2 sm:right-4 w-6 h-6 sm:w-8 sm:h-8 bg-black/50 hover:bg-black text-white rounded-full flex items-center justify-center transition-all duration-300 backdrop-blur-sm border border-white/20 text-sm sm:text-base z-50"
          >
            ✕
          </button>
        </div>

        {/* Carte de loterie avec particules */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full px-4 sm:px-0">
          {/* Particules rotatives - masquées sur mobile */}
          <div className="hidden sm:block">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className="particle"
                style={{
                  left: '50%',
                  top: '50%',
                  animationDelay: `${i * 0.15}s`,
                  transform: `rotate(${i * 30}deg) translateX(100px) rotate(-${i * 30}deg)`
                }}
              />
            ))}
          </div>

          {/* Particules qui s'envolent - masquées sur mobile */}
          <div className="hidden sm:block">
            {[...Array(20)].map((_, i) => (
              <div
                key={`spawn-${i}`}
                className="particle-spawn"
                style={{
                  left: '50%',
                  top: '50%',
                  '--tx': `${Math.cos(i * Math.PI / 10) * 100}px`,
                  '--ty': `${Math.sin(i * Math.PI / 10) * 100}px`,
                  animationDelay: `${Math.random() * 0.5}s`
                } as React.CSSProperties}
              />
            ))}
          </div>

          {/* Carte */}
          <div className={`${showAnimation ? 'animate-card-appear' : ''}`}>
            <div className="relative w-[280px] sm:w-[350px] md:w-[450px] h-[180px] sm:h-[220px] md:h-[280px] mx-auto overflow-hidden">
              {/* Image de fond de la carte */}
              <img 
                src="/carte.png" 
                alt="Lottery Card" 
                className="w-full h-full object-cover object-center transform transition-transform duration-300 hover:scale-105"
              />

              {/* Contenu superposé */}
              <div className="absolute inset-0">
                {/* Wallet Info en haut à droite */}
                <div className="absolute top-1 sm:top-2 right-2 sm:right-3">
                  <p className="font-mono text-[10px] sm:text-xs text-[#D4AF37] truncate drop-shadow-lg">
                    {walletConnected ? `${walletAddress.slice(0, 4)}...${walletAddress.slice(-4)}` : 'CGJr...3WNE'}
                  </p>
                </div>

                {/* Numéro de ticket en haut à gauche */}
                <div className="absolute top-1 sm:top-2 left-2 sm:left-3">
                  <p className="text-[10px] sm:text-xs font-medium text-[#D4AF37] drop-shadow-lg">
                    #{Math.floor(Math.random() * 10000).toString().padStart(4, '0')}
                  </p>
                </div>

                {/* Tasks avec espacement maximal */}
                <div className="absolute inset-x-0 bottom-2 sm:bottom-4 px-3 sm:px-6 space-y-2 sm:space-y-3.5">
                  {tasks.map(task => (
                    <div 
                      key={task.id}
                      className={`flex items-center space-x-2 sm:space-x-3 group ${task.completed ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      onClick={() => !task.completed && handleTwitterAction(task.id)}
                    >
                      <button
                        className={`w-3 sm:w-3.5 h-3 sm:h-3.5 rounded border-[1px] flex items-center justify-center transition-all duration-300
                          ${task.completed 
                            ? 'bg-[#D4AF37] border-[#D4AF37]' 
                            : 'border-[#D4AF37] group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37]/10'
                          }`}
                        disabled={task.completed}
                      >
                        {task.completed && <span className="text-black text-[6px] sm:text-[7px]">✓</span>}
                      </button>
                      <div className="flex items-center space-x-2 sm:space-x-4 w-full">
                        <span className={`text-[10px] sm:text-[12px] text-[#D4AF37] drop-shadow-lg transition-all duration-300 
                          ${task.completed ? 'text-[#D4AF37]/50' : 'group-hover:text-[#D4AF37] group-hover:brightness-125'}`}>
                          {task.title}
                        </span>
                        <span className={`text-[8px] sm:text-[10px] text-[#D4AF37]/60 italic transition-all duration-300 
                          ${task.completed ? 'text-[#D4AF37]/30' : 'group-hover:text-[#D4AF37]/80'} hidden sm:inline-block`}>
                          {task.completed ? '(completed)' : '(click & complete task to get 1 ticket)'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Effet pour charger les messages existants
  useEffect(() => {
    const messagesRef = collection(db, 'chat_messages');
    const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(50));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        content: doc.data().content,
        timestamp: doc.data().timestamp.toDate()
      }));
      setChatMessages(messages.reverse());
    });

    return () => unsubscribe();
  }, []);

  return (
    <div className="min-h-screen bg-retro-black text-retro-white relative overflow-hidden font-mono">
      <Background />
      
      {/* Effet de scan TV rétro */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(0,0,0,0.02)_50%)] bg-[length:100%_4px] animate-scan"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.2)_100%)]"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 border-b-2 border-retro-white/30 bg-retro-black">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="text-2xl font-bold tracking-wider border-2 border-retro-white px-4 py-1">
              SOUL AI
            </div>
            <button 
              onClick={walletConnected ? disconnectWallet : connectWallet}
              className="border-2 border-retro-white px-6 py-1 hover:bg-retro-white hover:text-retro-black transition-all duration-300"
            >
              {walletConnected ? `${walletAddress.slice(0, 4)}...${walletAddress.slice(-4)}` : 'CONNECT'}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 relative z-10">
        {/* About Section */}
        <div className="mb-16 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="border-2 border-retro-white p-8 bg-retro-black">
            <h2 className="text-2xl font-bold tracking-widest mb-6">SOUL AI</h2>
            <div className="space-y-6">
              <p className="text-lg leading-relaxed">
                Welcome to the first AI-powered lottery on Solana. Our revolutionary system uses artificial intelligence 
                to manage a $20,000 reward pool, ensuring fair and transparent distribution among our community members.
              </p>
              <p className="text-base text-retro-white/80">
                What makes us unique:
              </p>
              <ul className="list-disc list-inside space-y-2 text-retro-white/80">
                <li>AI-driven reward distribution system</li>
                <li>Transparent blockchain transactions</li>
                <li>Community-focused development</li>
                <li>Regular prize draws with instant payouts</li>
              </ul>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="border border-retro-white/30 p-4">
                  <div className="text-3xl font-bold mb-2">$20K</div>
                  <div className="text-sm tracking-wider">TOTAL POOL</div>
                </div>
                <div className="border border-retro-white/30 p-4">
                  <div className="text-3xl font-bold mb-2">AI</div>
                  <div className="text-sm tracking-wider">DISTRIBUTION</div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-2 border-retro-white p-8 bg-retro-black">
            <h2 className="text-2xl font-bold tracking-widest mb-6">HOW TO PARTICIPATE</h2>
            <div className="space-y-6">
              <p className="text-base text-retro-white/80">
                Join our growing community and get a chance to win from our prize pool. 
                Follow these simple steps to participate:
              </p>
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 border-2 border-retro-white flex items-center justify-center flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-2">Connect Your Wallet</h3>
                  <p className="text-sm text-retro-white/70">Use Phantom Wallet to participate in the lottery. New to Solana? 
                  We'll guide you through the setup process.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 border-2 border-retro-white flex items-center justify-center flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-2">Hold $SOUL Tokens</h3>
                  <p className="text-sm text-retro-white/70">Minimum 1M $SOUL tokens required. These tokens represent your 
                  stake in our ecosystem and grant you access to exclusive features.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 border-2 border-retro-white flex items-center justify-center flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-2">Complete Tasks</h3>
                  <p className="text-sm text-retro-white/70">Each completed task increases your winning chances. Our AI system 
                  tracks your engagement and adjusts your odds accordingly.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Timer Section */}
        <div className="mb-12 text-center">
          <div className="inline-block border-2 border-retro-white p-6 bg-retro-black">
            <h2 className="text-xl mb-4 tracking-widest">NEXT DRAW IN</h2>
            <div className="grid grid-cols-4 gap-4 text-2xl">
              <div className="border border-retro-white p-2">
                <div className="font-bold">{Math.floor(timeLeft / (24 * 60 * 60))}</div>
                <div className="text-sm">DAYS</div>
              </div>
              <div className="border border-retro-white p-2">
                <div className="font-bold">{Math.floor((timeLeft % (24 * 60 * 60)) / (60 * 60))}</div>
                <div className="text-sm">HOURS</div>
              </div>
              <div className="border border-retro-white p-2">
                <div className="font-bold">{Math.floor((timeLeft % (60 * 60)) / 60)}</div>
                <div className="text-sm">MIN</div>
              </div>
              <div className="border border-retro-white p-2">
                <div className="font-bold">{timeLeft % 60}</div>
                <div className="text-sm">SEC</div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="border-2 border-retro-white p-6 bg-retro-black text-center">
            <div className="text-4xl font-bold mb-2">{globalStats.total_participants}</div>
            <div className="tracking-widest">PARTICIPANTS</div>
          </div>
          <div className="border-2 border-retro-white p-6 bg-retro-black text-center">
            <div className="text-4xl font-bold mb-2">
              {globalStats.total_tickets}
            </div>
            <div className="tracking-widest">TOTAL TICKETS</div>
          </div>
          <div className="border-2 border-retro-white p-6 bg-retro-black text-center">
            <div className="text-4xl font-bold mb-2">5</div>
            <div className="tracking-widest">SOL PRIZE</div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Tasks & Contract */}
          <div className="space-y-8">
            {/* Contract Address */}
            <div className="border-2 border-retro-white p-6 bg-retro-black">
              <h3 className="text-lg mb-4 tracking-widest">CONTRACT ADDRESS</h3>
              <div className="font-mono text-sm break-all">
                TBA
              </div>
            </div>

            {/* Tasks Section */}
            <div className="border-2 border-retro-white p-6 bg-retro-black">
              <h3 className="text-lg mb-4 tracking-widest">TASKS</h3>
              <div className="space-y-4">
                {tasks.map(task => (
                  <div 
                    key={task.id}
                    className="flex items-center space-x-4 cursor-pointer group"
                    onClick={() => !task.completed && handleTwitterAction(task.id)}
                  >
                    <div className={`w-6 h-6 border-2 border-retro-white flex items-center justify-center transition-all duration-300
                      ${task.completed ? 'bg-retro-white text-retro-black' : 'group-hover:bg-retro-white/20'}`}>
                      {task.completed && '✓'}
                    </div>
                    <div className="flex-1">
                      <span className={`${task.completed ? 'line-through text-retro-white/50' : 'text-retro-white'}`}>
                        {task.title}
                      </span>
                      <span className="block text-xs text-retro-white/50 mt-1">
                        {task.completed ? 'Completed' : '+1 ticket reward'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Activity Feed */}
          <div className="border-2 border-retro-white bg-retro-black">
            <div className="p-6 border-b-2 border-retro-white/30">
              <h3 className="text-lg tracking-widest">ACTIVITY FEED</h3>
              <p className="text-sm text-retro-white/70 mt-2">Track real-time participation and system updates</p>
            </div>
            
            {/* Tabs */}
            <div className="grid grid-cols-2 border-b-2 border-retro-white/30">
              <button 
                onClick={() => setActiveTab('participants')}
                className={`p-4 text-sm tracking-wider border-r border-retro-white/30 transition-all duration-300
                  ${activeTab === 'participants' ? 'bg-retro-white/20' : 'hover:bg-retro-white/5'}`}
              >
                PARTICIPANTS
              </button>
              <button 
                onClick={() => setActiveTab('messages')}
                className={`p-4 text-sm tracking-wider transition-all duration-300
                  ${activeTab === 'messages' ? 'bg-retro-white/20' : 'hover:bg-retro-white/5'}`}
              >
                SYSTEM MESSAGES
              </button>
            </div>

            {/* Content */}
            <div className="h-[400px] overflow-y-auto custom-scrollbar">
              {activeTab === 'participants' ? (
                <div className="p-4 space-y-3">
                  {participants.map((participant, index) => (
                    <div key={index} className="flex items-center justify-between border border-retro-white/30 p-3 hover:border-retro-white/50 transition-all duration-300">
                      <div>
                        <span className="font-mono text-sm">
                          {participant.address.slice(0, 6)}...{participant.address.slice(-4)}
                        </span>
                        <span className="block text-xs text-retro-white/50 mt-1">
                          {formatDistance(participant.timestamp, new Date(), { addSuffix: true })}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-bold">{participant.tickets}</span>
                        <span className="text-xs ml-1">TICKETS</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 space-y-4">
                  {chatMessages.map((message, index) => (
                    <div key={index} className="border border-retro-white/30 p-4 hover:border-retro-white/50 transition-all duration-300">
                      <div className="text-xs text-retro-white/50 mb-2">
                        {formatDistance(message.timestamp, new Date(), { addSuffix: true })}
                      </div>
                      <div className="text-sm leading-relaxed">{message.content}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Lottery Card Modal */}
      {showLotteryCard && <LotteryCardModal />}

      {/* Custom Alert */}
      {alertMessage && (
        <CustomAlert
          message={alertMessage}
          onClose={() => setAlertMessage(null)}
        />
      )}
    </div>
  );
}

export default App; 